import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { BrandMark, BrandName } from "@/components/BrandMark";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/admin/login", { email, password });
      localStorage.setItem("arihant_admin_token", res.data.token);
      localStorage.setItem("arihant_admin_role", res.data.role || "admin");
      toast.success(`Welcome back, ${res.data.name || "Admin"}.`);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/admin/forgot-password", { email });
      setForgotSent(true);
      toast.success(res.data.message || "Reset link sent if the account exists.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not process the request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ink min-h-screen text-cream flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6" data-testid="admin-login-logo">
            <BrandMark size={40} iconSize={18} />
            <BrandName className="font-display text-2xl text-cream" />
          </Link>
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold">Admin Portal</p>
        </div>

        <form
          onSubmit={forgotMode ? submitForgot : submit}
          data-testid="admin-login-form"
          className="rounded-3xl border border-gold/20 bg-card/70 p-8 sm:p-10 space-y-5"
        >
          {forgotMode && (
            <p className="text-xs text-faded leading-relaxed" data-testid="forgot-intro">
              Enter your admin email — we will send a one-time reset link (valid for 1 hour).
            </p>
          )}
          <div>
            <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="field-input"
              placeholder="travelrushpune@gmail.com"
            />
          </div>
          {!forgotMode && (
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="field-input"
                placeholder="••••••••"
              />
            </div>
          )}

          {forgotSent && forgotMode && (
            <p className="text-sm text-emerald-300" data-testid="forgot-success">
              If an account exists for this email, a reset link has been sent. Check your inbox (and spam folder).
            </p>
          )}
          {error && (
            <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid={forgotMode ? "forgot-submit-btn" : "admin-login-submit-btn"}
            className="w-full flex items-center justify-center gap-2 bg-gold text-ink font-semibold text-sm px-8 py-4 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
          >
            <Lock size={15} />
            {loading ? (forgotMode ? "Sending…" : "Signing in…") : forgotMode ? "Send Reset Link" : "Sign In"}
          </button>

          <p className="text-center">
            <button
              type="button"
              onClick={() => { setForgotMode(!forgotMode); setError(""); setForgotSent(false); }}
              className="text-xs text-mist hover:text-gold transition-colors"
              data-testid="forgot-password-toggle"
            >
              {forgotMode ? "← Back to sign in" : "Forgot password?"}
            </button>
          </p>

          <p className="text-center">
            <Link to="/" className="text-xs text-faded hover:text-gold transition-colors" data-testid="admin-back-home-link">
              ← Back to site
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
